import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, BLOCKABLE_ACTION_KEYS, ACTION_THRESHOLDS } from '../../services/api';
import type { AdminUserDetail, AdminUserJobSummary, AdminUserResumeSummary } from '../../services/api';
import { ArrowLeft, Save, Loader2, Briefcase, FileText, Gift, ShieldCheck, X, AlertCircle, CheckCircle } from 'lucide-react';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

const EDITABLE_FIELDS = [
  { key: 'role', label: 'Role', options: ['user', 'admin', 'snappy', 'test'] },
  { key: 'status', label: 'Status', options: ['trial', 'active', 'hibernating', 'cancelled', 'expired'] },
  { key: 'plan', label: 'Plan', options: ['free', 'consumer', 'enterprise'] },
  { key: 'org_id', label: 'Organization ID', type: 'text' },
] as const;

export function UserDetail() {
  const { userid } = useParams<{ userid: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadUser = useCallback(() => {
    if (!userid) return;
    setLoading(true);
    api.getUser(userid)
      .then(setData)
      .catch(() => setMessage({ type: 'error', text: 'Failed to load user' }))
      .finally(() => setLoading(false));
  }, [userid]);

  useEffect(() => { loadUser(); }, [loadUser]);
  useAutoRefresh(loadUser);

  const handleFieldChange = (key: string, value: string) => {
    setEdits(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (Object.keys(edits).length === 0) return;

    const confirmed = window.confirm(`Update ${Object.keys(edits).length} field(s)?`);
    if (!confirmed) return;

    setSaving(true);
    setMessage(null);
    try {
      await api.updateUser(userid!, edits);
      setMessage({ type: 'success', text: 'User updated successfully' });
      setEdits({});
      // Reload
      const refreshed = await api.getUser(userid!);
      setData(refreshed);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setMessage({ type: 'error', text: axiosErr?.response?.data?.message || 'Failed to update user' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
      </div>
    );
  }

  const user = data?.user || {};

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button
        onClick={() => navigate('/admin/f47ac10b58cc/users')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Users
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{user.email || 'Unknown User'}</h1>
          <p className="text-sm text-gray-500">{user.userhandle} &middot; {user.userid}</p>
        </div>
        {Object.keys(edits).length > 0 && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes ({Object.keys(edits).length})
          </button>
        )}
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {message.text}
        </div>
      )}

      {/* Editable Fields */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Account Settings</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {EDITABLE_FIELDS.map(field => {
            const currentValue = edits[field.key] ?? user[field.key] ?? '';
            const isEdited = field.key in edits;

            if ('options' in field) {
              return (
                <div key={field.key}>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    {field.label} {isEdited && <span className="text-primary-600">*</span>}
                  </label>
                  <select
                    value={currentValue}
                    onChange={(e) => handleFieldChange(field.key, e.target.value)}
                    className={`w-full border rounded-lg px-3 py-2 ${isEdited ? 'border-primary-500 bg-primary-50' : 'border-gray-300'}`}
                  >
                    <option value="">-</option>
                    {field.options.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              );
            }

            return (
              <div key={field.key}>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  {field.label} {isEdited && <span className="text-primary-600">*</span>}
                </label>
                <input
                  type="text"
                  value={currentValue}
                  onChange={(e) => handleFieldChange(field.key, e.target.value)}
                  className={`w-full border rounded-lg px-3 py-2 ${isEdited ? 'border-primary-500 bg-primary-50' : 'border-gray-300'}`}
                />
              </div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h3 className="text-sm font-medium text-gray-600 mb-2">Quick Actions</h3>
          <div className="flex gap-2">
            {(edits['plan'] ?? user['plan']) !== 'free' ? (
              <button
                onClick={() => {
                  setEdits(prev => ({
                    ...prev,
                    plan: 'free',
                    status: 'active',
                  }));
                }}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
              >
                <Gift className="w-4 h-4" />
                Grant Free Plan
              </button>
            ) : (
              <span className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg">
                <Gift className="w-4 h-4" />
                Free Plan (pending save)
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Read-only Fields */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">User Info (Read Only)</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          {['userid', 'email', 'userhandle', 'sub_provider', 'sub_id', 'stripe_customer_id', 'stripe_subscription_id', 'marketing_email_optin', 'createdAt', 'updatedAt'].map(key => (
            user[key] !== undefined && (
              <div key={key} className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">{key}</span>
                <span className={`font-mono text-gray-800${key === 'userid' ? '' : ' truncate max-w-[60%]'}`}>{String(user[key])}</span>
              </div>
            )
          ))}
        </div>
      </div>

      {/* Execution Limits */}
      <ExecutionLimits
        user={user}
        userid={userid!}
        onSaved={async () => {
          const refreshed = await api.getUser(userid!);
          setData(refreshed);
        }}
      />

      {/* Jobs Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Briefcase className="w-5 h-5" />
            Jobs ({data?.job_count || 0})
          </h2>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {(data?.jobs_summary || []).map((job: AdminUserJobSummary) => (
              <div key={job.jobid} className="flex items-center justify-between text-sm py-1 border-b border-gray-100">
                <span className="truncate">{job.jobtitle || job.jobcompany || job.jobid}</span>
                <span className="text-xs px-2 py-0.5 rounded bg-gray-100">{job.jobphase}</span>
              </div>
            ))}
          </div>
          {data && data.summary_limit !== undefined && data.job_count > data.jobs_summary.length && (
            <p className="text-xs text-gray-500 mt-2">
              Showing {data.jobs_summary.length} of {data.job_count}.
            </p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Resumes ({data?.resume_count || 0})
          </h2>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {(data?.resumes_summary || []).map((resume: AdminUserResumeSummary) => (
              <div key={resume.resumename} className="flex items-center justify-between text-sm py-1 border-b border-gray-100">
                <span>{resume.resumename}</span>
                <span className="text-xs text-gray-500">{resume.lastupdate ? new Date(resume.lastupdate).toLocaleDateString() : '-'}</span>
              </div>
            ))}
          </div>
          {data && data.summary_limit !== undefined && data.resume_count > data.resumes_summary.length && (
            <p className="text-xs text-gray-500 mt-2">
              Showing {data.resumes_summary.length} of {data.resume_count}.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}


interface ExecutionLimitsProps {
  user: Record<string, unknown>;
  userid: string;
  onSaved: () => Promise<void> | void;
}

type BlockScope = 'ongoing' | 'until_cycle';

function ExecutionLimits({ user, userid, onSaved }: ExecutionLimitsProps) {
  const ongoingBlocked = useMemo<string[]>(() => {
    const raw = user['blocked_actions'];
    return Array.isArray(raw) ? (raw as string[]) : [];
  }, [user]);

  const untilCycleBlocked = useMemo<string[]>(() => {
    const raw = user['blocked_actions_until_cycle'];
    return Array.isArray(raw) ? (raw as string[]) : [];
  }, [user]);

  const initialAutoBlock = useMemo<boolean>(() => {
    return Boolean(user['auto_block_excessive']);
  }, [user]);

  const usageCounters = useMemo<Record<string, Record<string, number>>>(() => {
    const raw = user['usage_counters'];
    if (raw && typeof raw === 'object') return raw as Record<string, Record<string, number>>;
    return {};
  }, [user]);

  const currentMonth = useMemo(() => {
    const now = new Date();
    return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
  }, []);

  // Counters reset at the next UTC month boundary.
  const refreshDate = useMemo(() => {
    const now = new Date();
    const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
    const mm = String(next.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(next.getUTCDate()).padStart(2, '0');
    return `${mm}/${dd}/${next.getUTCFullYear()}`;
  }, []);

  const monthCounters = usageCounters[currentMonth] || {};

  const [pendingAction, setPendingAction] = useState<{
    key: string;
    willBlock: boolean;
    currentScope: BlockScope | null;
  } | null>(null);
  const [pendingScope, setPendingScope] = useState<BlockScope>('ongoing');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [autoBlock, setAutoBlock] = useState<boolean>(initialAutoBlock);
  const [autoBlockSaving, setAutoBlockSaving] = useState(false);

  // Keep local autoBlock in sync if the user record reloads.
  useEffect(() => {
    setAutoBlock(initialAutoBlock);
  }, [initialAutoBlock]);

  function scopeOf(key: string): BlockScope | null {
    if (ongoingBlocked.includes(key)) return 'ongoing';
    if (untilCycleBlocked.includes(key)) return 'until_cycle';
    return null;
  }

  function startToggle(key: string) {
    const current = scopeOf(key);
    setPendingAction({ key, willBlock: current === null, currentScope: current });
    setPendingScope('ongoing');
    setReason('');
    setError(null);
  }

  async function confirmToggle() {
    if (!pendingAction) return;
    if (!reason.trim()) {
      setError('Reason is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const ongoingSet = new Set(ongoingBlocked);
      const untilCycleSet = new Set(untilCycleBlocked);
      ongoingSet.delete(pendingAction.key);
      untilCycleSet.delete(pendingAction.key);
      if (pendingAction.willBlock) {
        if (pendingScope === 'ongoing') ongoingSet.add(pendingAction.key);
        else untilCycleSet.add(pendingAction.key);
      }
      await api.updateUserBlockedActions(userid, {
        ongoing_action_keys: Array.from(ongoingSet),
        until_cycle_action_keys: Array.from(untilCycleSet),
        auto_block_excessive: autoBlock,
        reason: reason.trim(),
      });
      setSuccess(
        pendingAction.willBlock
          ? `Blocked '${pendingAction.key}' (${pendingScope === 'ongoing' ? 'ongoing' : 'until next cycle'})`
          : `Unblocked '${pendingAction.key}'`,
      );
      setTimeout(() => setSuccess(null), 3000);
      setPendingAction(null);
      setReason('');
      await onSaved();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to update blocked actions');
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleAutoBlock(next: boolean) {
    const note = window.prompt(
      next
        ? 'Enable auto-block on excessive usage. Enter a reason for the audit log:'
        : 'Disable auto-block on excessive usage. Enter a reason for the audit log:',
    );
    if (note === null) return; // cancelled
    if (!note.trim()) {
      setError('Reason is required');
      return;
    }
    setAutoBlockSaving(true);
    setError(null);
    try {
      await api.updateUserBlockedActions(userid, {
        ongoing_action_keys: ongoingBlocked,
        until_cycle_action_keys: untilCycleBlocked,
        auto_block_excessive: next,
        reason: note.trim(),
      });
      setAutoBlock(next);
      setSuccess(next ? 'Auto-block enabled' : 'Auto-block disabled');
      setTimeout(() => setSuccess(null), 3000);
      await onSaved();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to update auto-block');
    } finally {
      setAutoBlockSaving(false);
    }
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
        <ShieldCheck className="w-5 h-5" />
        Execution Limits
      </h2>
      <p className="text-xs text-gray-500 mb-4">
        Block specific user actions. Counts shown are for the current UTC month ({currentMonth}).
      </p>

      <label className="flex items-center gap-3 mb-4 px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 cursor-pointer">
        <input
          type="checkbox"
          checked={autoBlock}
          disabled={autoBlockSaving}
          onChange={(e) => toggleAutoBlock(e.target.checked)}
          className="w-4 h-4"
        />
        <span className="flex flex-col">
          <span className="text-sm font-medium text-gray-900">
            Auto-block user on excessive usage
          </span>
          <span className="text-xs text-gray-500">
            When enabled, crossing a per-month threshold automatically adds that action to the
            user's ongoing blocks. Persists until manually changed.
          </span>
        </span>
      </label>

      {success && (
        <div className="flex items-center p-3 mb-4 bg-green-50 text-green-700 rounded-lg text-sm">
          <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {BLOCKABLE_ACTION_KEYS.map(key => {
          const scope = scopeOf(key);
          const isBlocked = scope !== null;
          const count = Number(monthCounters[key] || 0);
          return (
            <div
              key={key}
              className={`flex items-center justify-between px-4 py-3 rounded-lg border ${
                isBlocked ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex flex-col">
                <span className="font-mono text-sm text-gray-900">{key}</span>
                <span className="text-xs text-gray-500">
                  {count} of {ACTION_THRESHOLDS[key] ?? '—'} this month · refresh {refreshDate}
                  {scope === 'until_cycle' && ' · clears next cycle'}
                </span>
              </div>
              <button
                onClick={() => startToggle(key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  isBlocked
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {isBlocked
                  ? scope === 'until_cycle' ? 'Blocked (cycle)' : 'Blocked'
                  : 'Allowed'}
              </button>
            </div>
          );
        })}
      </div>

      {pendingAction && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => !submitting && setPendingAction(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {pendingAction.willBlock ? 'Block' : 'Unblock'}{' '}
                <code className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded">
                  {pendingAction.key}
                </code>
              </h3>
              <button
                onClick={() => !submitting && setPendingAction(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {pendingAction.willBlock && (
              <div className="mb-4">
                <span className="block text-sm font-medium text-gray-700 mb-2">Block scope</span>
                <label className="flex items-start gap-2 mb-2 cursor-pointer">
                  <input
                    type="radio"
                    name="block-scope"
                    value="ongoing"
                    checked={pendingScope === 'ongoing'}
                    onChange={() => setPendingScope('ongoing')}
                    className="mt-0.5"
                  />
                  <span className="text-sm">
                    <span className="font-medium text-gray-900">Block ongoing</span>
                    <span className="block text-xs text-gray-500">
                      Persists until an admin removes it.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="block-scope"
                    value="until_cycle"
                    checked={pendingScope === 'until_cycle'}
                    onChange={() => setPendingScope('until_cycle')}
                    className="mt-0.5"
                  />
                  <span className="text-sm">
                    <span className="font-medium text-gray-900">Block only until next monthly cycle</span>
                    <span className="block text-xs text-gray-500">
                      Auto-released on the user's next 30-day refill pass.
                    </span>
                  </span>
                </label>
              </div>
            )}

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason (required)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Why is this change being made?"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3"
              autoFocus
            />

            {error && (
              <div className="flex items-center p-2 mb-3 bg-red-50 text-red-700 rounded text-sm">
                <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                {error}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPendingAction(null)}
                disabled={submitting}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={confirmToggle}
                disabled={submitting || !reason.trim()}
                className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 text-sm flex items-center gap-2 ${
                  pendingAction.willBlock
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-primary-600 hover:bg-primary-700'
                }`}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
